import { graphql } from "babel-plugin-relay/macro";
import { Button } from "primereact/button";
import { ReactNode } from "react";
import { useFragment, useMutation } from "react-relay";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { ProjectContentElementContainer_ContentSubmissionFragment$key } from "@generated/ProjectContentElementContainer_ContentSubmissionFragment.graphql";
import { ProjectContentElementContainer_GoBackElementWiseMutation } from "@generated/ProjectContentElementContainer_GoBackElementWiseMutation.graphql";
import { WrapperContainerContentSubmission } from "../../app/containers/WrapperContainerContentSubmission";
import { footerPixelsRightFrame } from "../../infecto-lms-webapp/hooks/scrollbarComponentHelpers";
import { getLearnOpportunityPath } from "../router/routes/auth/nodes.routes";

const CONTENT_SUBMISSION_FRAGMENT = graphql`
	fragment ProjectContentElementContainer_ContentSubmissionFragment on ContentSubmission {
		id
		definition {
			... on ActiveELearningContentSubmissionDefinition {
				canGoToPrevious
				progress {
					percentage
				}
			}
		}
		learnOpportunity {
			id
			root {
				id
			}
			typeDefinition {
				... on LearnOpportunityELearningContentTypeDefinition {
					containedElementTypes
				}
			}
			...AttachmentDisplay_LearnOpportunityV2Fragment
		}
	}
`;

const GO_BACK_ELEMENT_WISE_MUTATION = graphql`
	mutation ProjectContentElementContainer_GoBackElementWiseMutation(
		$input: GoToPreviousElementInput!
	) {
		ELearning {
			goToPreviousElement(input: $input) {
				contentSubmission {
					...RightFrameContentElement_ContentSubmissionFragment
				}
			}
		}
	}
`;

interface OwnProps {
	hideBack?: boolean;
	contentSubmissionFragmentRef: ProjectContentElementContainer_ContentSubmissionFragment$key;
	children: any; //eslint-disable-line  @typescript-eslint/no-explicit-any
	nextButton: ReactNode;
	footer?: ReactNode;
}

export function ProjectContentElementContainer({
	hideBack,
	contentSubmissionFragmentRef,
	children,
	nextButton,
	footer,
}: OwnProps) {
	const contentSubmission =
		useFragment<ProjectContentElementContainer_ContentSubmissionFragment$key>(
			CONTENT_SUBMISSION_FRAGMENT,
			contentSubmissionFragmentRef,
		);

	const [goBack] = useMutation<ProjectContentElementContainer_GoBackElementWiseMutation>(
		GO_BACK_ELEMENT_WISE_MUTATION,
	);

	const navigate = useNavigate();
	const hasMultipleChoice =
		contentSubmission.learnOpportunity?.typeDefinition.containedElementTypes?.includes(
			"multipleChoice",
		) ||
		contentSubmission.learnOpportunity?.typeDefinition.containedElementTypes?.includes(
			"randomMultipleChoice",
		);
	const hasHeft =
		contentSubmission.learnOpportunity?.typeDefinition.containedElementTypes?.includes("file");
	return (
		<DivContainer
			hasMultipleChoiceOrHeft={hasMultipleChoice || hasHeft || false}
			className="flex flex-grow-1 flex-column md:mr-2 justify-content-center w-full"
		>
			<WrapperContainerContentSubmission
				width={100}
				marginBottom={0}
				footerPixels={footerPixelsRightFrame(hasMultipleChoice || false)}
			>
				<div className="flex-grow-1">{children}</div>
				{hasMultipleChoice && (
					<div>
						<div className="p-3 md:pt-4 md:px-5 flex">
							<div className="flex text-center w-full justify-content-center">
								{!hideBack && contentSubmission.learnOpportunity && (
									<Button
										label="ZURÜCK"
										className={`text-black-alpha-90 mr-2 md:mr-4 pl-6 pr-6 p-button-outlined p-button-secondary bg-white`}
										disabled={!contentSubmission.definition.canGoToPrevious}
										onClick={() => {
											if (contentSubmission.definition.canGoToPrevious) {
												goBack({
													variables: { input: { contentSubmissionId: contentSubmission.id } },
												});
											} else {
												navigate(
													getLearnOpportunityPath(contentSubmission.learnOpportunity!.root!.id),
												);
											}
										}}
									/>
								)}
								{nextButton}
							</div>
						</div>
						{footer}
					</div>
				)}
			</WrapperContainerContentSubmission>
		</DivContainer>
	);
}
interface DivProps {
	hasMultipleChoiceOrHeft: boolean;
}
const DivContainer = styled.div<DivProps>`
	max-height: 100%;
	padding-bottom: ${(props) => (props.hasMultipleChoiceOrHeft ? 200 : 20)}px;
`;
